import React from 'react';
import { Box, useTheme } from '@mui/material';

const ANIMATION_STYLES = `
  @keyframes companyPickerFloat {
    0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
    33% { transform: translate3d(18px, -22px, 0) scale(1.04); }
    66% { transform: translate3d(-14px, 12px, 0) scale(0.97); }
  }
  @keyframes companyPickerDrift {
    0% { transform: translate3d(0, 0, 0); }
    50% { transform: translate3d(-2%, 1.5%, 0); }
    100% { transform: translate3d(0, 0, 0); }
  }
  @keyframes companyPickerPulse {
    0%, 100% { opacity: 0.45; transform: scale(1); }
    50% { opacity: 0.75; transform: scale(1.08); }
  }
  @keyframes companyPickerIconPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  @keyframes companyPickerShimmer {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }
  @keyframes companyPickerGrid {
    0% { transform: translate3d(0, 0, 0); }
    100% { transform: translate3d(48px, 48px, 0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .company-picker-ambient *,
    .company-picker-ambient *::before,
    .company-picker-ambient *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
    }
  }
`;

/**
 * Shared slate-blue ambient backdrop for company picker screens.
 * Uses theme palette tokens so dark mode matches Jobs / app chrome.
 */
const CompanyPickerAmbientBg = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { default: bgDefault, paper: bgPaper } = theme.palette.background;
  const divider = theme.palette.divider;

  const pageBg = isDarkMode
    ? `linear-gradient(145deg, ${bgDefault} 0%, ${bgPaper} 48%, ${bgDefault} 100%)`
    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)';

  const orbRgba = (r, g, b, a) =>
    `radial-gradient(circle, rgba(${r}, ${g}, ${b}, ${a}) 0%, rgba(${r}, ${g}, ${b}, 0) 70%)`;

  return (
    <Box className="company-picker-ambient" aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: pageBg,
        }}
      />

      {/* Soft aurora wash */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: '-20%',
          background: isDarkMode
            ? `linear-gradient(120deg, transparent 20%, rgba(59, 130, 246, 0.08) 45%, rgba(148, 163, 184, 0.06) 60%, transparent 80%)`
            : `linear-gradient(120deg, transparent 25%, rgba(59, 130, 246, 0.06) 50%, transparent 75%)`,
          backgroundSize: '200% 200%',
          animation: 'companyPickerShimmer 18s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Subtle drifting grid */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: '-48px',
          opacity: isDarkMode ? 0.22 : 0.35,
          backgroundImage: isDarkMode
            ? `linear-gradient(${divider} 1px, transparent 1px), linear-gradient(90deg, ${divider} 1px, transparent 1px)`
            : 'linear-gradient(rgba(148, 163, 184, 0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.35) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 20%, transparent 75%)',
          animation: 'companyPickerGrid 40s linear infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Floating orbs — primary blue + slate */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '6%',
          left: '4%',
          width: { xs: 160, md: 240 },
          height: { xs: 160, md: 240 },
          borderRadius: '50%',
          background: isDarkMode
            ? orbRgba(59, 130, 246, 0.22)
            : orbRgba(59, 130, 246, 0.16),
          filter: 'blur(2px)',
          animation: 'companyPickerFloat 9s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '48%',
          right: '2%',
          width: { xs: 120, md: 180 },
          height: { xs: 120, md: 180 },
          borderRadius: '50%',
          background: isDarkMode
            ? orbRgba(148, 163, 184, 0.18)
            : orbRgba(37, 99, 235, 0.12),
          filter: 'blur(1px)',
          animation: 'companyPickerFloat 11s ease-in-out infinite',
          animationDelay: '1.5s',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: '8%',
          left: '22%',
          width: { xs: 140, md: 200 },
          height: { xs: 140, md: 200 },
          borderRadius: '50%',
          background: isDarkMode
            ? orbRgba(37, 99, 235, 0.16)
            : orbRgba(59, 130, 246, 0.1),
          animation: 'companyPickerFloat 10s ease-in-out infinite',
          animationDelay: '3s',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '28%',
          left: '55%',
          width: 90,
          height: 90,
          borderRadius: '50%',
          background: isDarkMode
            ? orbRgba(96, 165, 250, 0.2)
            : orbRgba(59, 130, 246, 0.14),
          animation: 'companyPickerPulse 7s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      {/* Slow vignette drift for depth */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDarkMode
            ? `radial-gradient(ellipse 80% 70% at 50% 0%, transparent 40%, ${bgDefault} 100%)`
            : 'radial-gradient(ellipse 80% 70% at 50% 0%, transparent 45%, rgba(241, 245, 249, 0.85) 100%)',
          animation: 'companyPickerDrift 22s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <style>{ANIMATION_STYLES}</style>
    </Box>
  );
};

/** Shared glass / surface styles aligned with app dark slate palette. */
export const useCompanyPickerSurfaces = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const glassCard = {
    borderRadius: 4,
    background: isDarkMode
      ? 'linear-gradient(145deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.88) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.65) 100%)',
    backdropFilter: 'blur(20px)',
    border: isDarkMode
      ? `1px solid ${theme.palette.divider}`
      : '1px solid rgba(255, 255, 255, 0.9)',
    boxShadow: isDarkMode
      ? '0 8px 32px rgba(15, 23, 42, 0.55)'
      : '0 8px 32px rgba(15, 23, 42, 0.08)',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const companyCardHover = {
    '&:hover': {
      transform: 'translateY(-6px)',
      background: isDarkMode
        ? 'linear-gradient(145deg, rgba(51, 65, 85, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 100%)',
      boxShadow: isDarkMode
        ? '0 20px 48px rgba(15, 23, 42, 0.65), 0 0 0 1px rgba(59, 130, 246, 0.25)'
        : '0 20px 50px rgba(15, 23, 42, 0.12)',
      border: isDarkMode
        ? '1px solid rgba(59, 130, 246, 0.35)'
        : '1px solid rgba(59, 130, 246, 0.25)',
    },
  };

  const dialogPaperSx = {
    borderRadius: 3,
    background: isDarkMode ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(20px)',
    border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid #e2e8f0',
    boxShadow: isDarkMode
      ? '0 24px 64px rgba(15, 23, 42, 0.7)'
      : '0 24px 64px rgba(15, 23, 42, 0.15)',
  };

  const primaryCtaSx = {
    background: isDarkMode
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
      : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    boxShadow: isDarkMode
      ? '0 4px 20px rgba(59, 130, 246, 0.35)'
      : '0 4px 20px rgba(59, 130, 246, 0.35)',
    '&:hover': {
      background: isDarkMode
        ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
        : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    },
  };

  return { isDarkMode, theme, glassCard, companyCardHover, dialogPaperSx, primaryCtaSx };
};

export default CompanyPickerAmbientBg;
